
import { ReactSortable } from "react-sortablejs";
import { useMemo } from "react";
import { LayoutItem, sizeToGridClass } from "./constants";
import { useComponentMap } from "./widgets/ComponentMap";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";

interface DashboardWidgetsProps {
  widgetItems: LayoutItem[];
  handlesVisible: boolean;
  onRemove: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
  onReorder: (newOrder: LayoutItem[]) => void;
  stats: any;
  newUsersTimeline: any[];
  errorStats: any[];
  emailStats: any[];
  recentErrors: any[];
  activeUsers: any[];
  hasData: boolean;
}

interface DashboardItem extends LayoutItem {
  component: JSX.Element;
}

export function DashboardWidgets({
  widgetItems,
  handlesVisible,
  onRemove,
  onResize,
  onReorder,
  stats,
  newUsersTimeline,
  errorStats,
  emailStats,
  recentErrors,
  activeUsers,
  hasData
}: DashboardWidgetsProps) {
  // Use our component map hook to get all widget components
  const componentMap = useComponentMap(
    stats,
    newUsersTimeline,
    errorStats,
    emailStats,
    recentErrors,
    activeUsers,
    handlesVisible,
    onRemove,
    onResize
  );
  
  // Create dashboard items from active widget IDs
  const dashboardItems = useMemo(() => 
    widgetItems
      .filter(item => componentMap[item.id])
      .map(item => ({
        ...item,
        component: componentMap[item.id](item)
      })),
    [widgetItems, componentMap]
  );

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
        <h3 className="text-xl font-medium text-gray-700 mb-3">No admin data yet</h3>
        <p className="text-gray-500 mb-6 max-w-md">
          The admin dashboard will populate with insights as users register and use the system.
        </p>
        <Button onClick={() => window.location.href = '/admin/users'}>
          <UserPlus className="mr-2 h-4 w-4" />
          Manage users
        </Button>
      </div>
    );
  }

  return (
    <ReactSortable
      list={dashboardItems}
      setList={(newState) => {
        console.log("ReactSortable for dashboard items called with:", newState);
        const updatedItems = [...newState];
        onReorder(updatedItems);
      }}
      className="grid grid-cols-4 gap-4"
      animation={200}
      handle=".cursor-grab"
      ghostClass="opacity-50"
      disabled={!handlesVisible}
      group="widgets"
    >
      {dashboardItems.map(item => (
        <div 
          key={item.id} 
          className={`mb-4 ${sizeToGridClass[item.size] || 'col-span-1'}`}
        >
          {item.component}
        </div>
      ))}
    </ReactSortable>
  );
}
